import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";

import type { TargetJoints } from "api/services/content/schemas/shared";
import type { ContentType } from "routes/library/views/Create";
import { DeleteIcon } from "shared/icons";
import { DARK_BLUE } from "theme/colors";
import { translatePainLocation } from "utils/translations";

import { JointTag } from "../SharedStyledComponents";

import type { AuthorType, MediaType } from "./components";
import { AddIcon, Author, MediaTypeTag } from "./components";

const TAG_LIMIT = 3;

interface Props {
  author?: AuthorType;
  caption?: string;
  checked: boolean;
  className?: string;
  disabled?: boolean;
  id?: number;
  image?: React.ReactNode;
  isNew?: boolean;
  itemType?: ContentType;
  mediaType?: MediaType;
  showAddButton?: boolean;
  tags?: string[];
  title?: string;
  onClick?: (id: number) => void;
  onDelete?: () => void;
  onMoreInfoClick?: () => void;
}

export const LibraryItem: React.FC<Props> = ({
  author,
  caption,
  checked,
  className,
  disabled = false,
  id,
  image,
  isNew,
  itemType,
  mediaType,
  showAddButton,
  tags = [],
  title,
  onClick,
  onDelete,
  onMoreInfoClick,
}) => {
  const { t } = useTranslation();

  const translatedTags = tags?.map(tag => translatePainLocation({ painLocation: tag as TargetJoints }));

  const tagCount = translatedTags.length;

  return (
    <Container
      className={className}
      $disabled={disabled}
      onClick={() => {
        if (disabled) return;
        if (onClick && id) {
          onClick(id);
        }
      }}
    >
      <TopRow>
        {image && (
          <ThumbWrapper $itemType={itemType}>
            <Image>{image}</Image>
            {mediaType && <MediaTypeTag mediaType={mediaType} />}
          </ThumbWrapper>
        )}
        <Stack>
          <TitleRow>
            {title && <Title>{title}</Title>}
            {isNew && <NewBadge>{t("messages.new")}</NewBadge>}
          </TitleRow>
          <CaptionRow>
            {caption && (
              <Typography variant="subtitle1" color={DARK_BLUE[70]}>
                {caption}
              </Typography>
            )}
          </CaptionRow>

          {translatedTags.length > 0 && (
            <Tags>
              {translatedTags.slice(0, TAG_LIMIT).map(tag => (
                <JointTag key={tag}>{tag}</JointTag>
              ))}
              {tagCount > TAG_LIMIT && <TagRestCount>+{tagCount - TAG_LIMIT}</TagRestCount>}
            </Tags>
          )}

          {!disabled && onMoreInfoClick && (
            <Catcher
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <LinkText onClick={onMoreInfoClick} role="button">
                {t("usercourse.add.more_info")}
              </LinkText>
            </Catcher>
          )}
        </Stack>
        {!disabled && onDelete && (
          <ButtonContainer
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <DeleteIcon />
          </ButtonContainer>
        )}
        {showAddButton && (
          <ButtonContainer>
            <AddIcon checked={checked} />
          </ButtonContainer>
        )}
      </TopRow>

      <BottomRow>
        <Author author={author} />
      </BottomRow>
    </Container>
  );
};

export const Container = styled.div<{ $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.redesign.db5};
  box-shadow: 0 2px 4px 0 rgba(39, 53, 70, 0.05);
  border-radius: 12px;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.redesign.b100};
    background-color: ${({ theme }) => theme.colors.redesign.b10};
  }

  ${({ theme }) => theme.aboveBreakpoint} {
    width: calc(100% / 3 - 32px);
  }

  min-width: 240px;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const TopRow = styled(Row)`
  gap: 12px;
`;

const CaptionRow = styled(Row)`
  align-items: center;
  gap: 8px;
`;

const BottomRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
`;

const TitleRow = styled(Row)`
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const Image = styled.div`
  height: 80px;

  & svg {
    height: 80px;
    width: 80px;
  }
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const NewBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 11px;
  text-transform: uppercase;
  padding: 4px 8px;
  background: ${({ theme }) => theme.colors.redesign.b100};
  color: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
`;

export const Tags = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const TagRestCount = styled(JointTag)`
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.redesign.db20};
`;

export const ButtonContainer = styled.div`
  align-self: flex-start;
  margin-left: auto;

  &:hover {
    svg {
      color: ${({ theme }) => theme.colors.redesign.b100};
    }
  }
`;

const LinkText = styled.div`
  color: #5096e8;
  font-size: 12px;
  font-weight: 600;
`;

const Catcher = styled.div`
  margin-top: 4px;
`;

const ThumbWrapper = styled.div<{ $itemType?: ContentType }>`
  position: relative;
  width: 80px;
  height: 80px;
  overflow: hidden;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.white};
  ${({ $itemType }) =>
    $itemType === "exercise" &&
    css`
      background-color: ${({ theme }) => theme.colors.redesign.o10};
    `}
  ${({ $itemType }) =>
    $itemType === "lesson" &&
    css`
      background-color: ${({ theme }) => theme.colors.redesign.t10};
    `}
`;
